import { Outlet, useLocation, Link } from 'react-router-dom';
import { useState, useEffect, useCallback, useRef } from 'react';
import './DashboardLayout.css';
import { formatMonthYear } from '../utils/dateUtils';
import { useBrand } from '../context/BrandContext';

// Session timeout settings
const INACTIVITY_TIMEOUT = 15 * 60 * 1000; // 15 minutes

function DashboardLayout() {
    const { logoUrl: LOGO_URL, logoAlt: LOGO_ALT } = useBrand();
    const location = useLocation();
    const [showLogoutWarning, setShowLogoutWarning] = useState(false);
    const [showNotifications, setShowNotifications] = useState(false);
    const [showProfile, setShowProfile] = useState(false);
    const [isSidebarMobileOpen, setIsSidebarMobileOpen] = useState(false);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [isLocationSaved, setIsLocationSaved] = useState(() => sessionStorage.getItem('locationFetched') === 'true');
    const [hasActiveLoan, setHasActiveLoan] = useState(false);
    const [loanStats, setLoanStats] = useState({ active: 0, pending: 0 });
    const lastActivityRef = useRef(Date.now());
    const notificationsRef = useRef(null);
    const profileRef = useRef(null);

    const [notifications] = useState([
        {
            id: 1,
            type: 'payment',
            title: 'EMI Due Soon',
            message: 'Your Personal Loan EMI of ₹15,234 is due on 15 Dec 2025',
            time: '2 hours ago',
            read: false
        },
        {
            id: 2,
            type: 'success',
            title: 'Loan Application Approved',
            message: 'Your Two-Wheeler loan application has been approved!',
            time: '1 day ago',
            read: false
        },
        {
            id: 3,
            type: 'info',
            title: 'Credit Score Updated',
            message: 'Your credit score increased by 12 points this month',
            time: '2 days ago',
            read: true
        }
    ]);

    const getSessionUser = () => {
        try {
            return JSON.parse(localStorage.getItem('userSession')) || {};
        } catch {
            return {};
        }
    };

    const [user, setUser] = useState({
        name: getSessionUser().name || 'Loading...',
        email: '',
        phone: getSessionUser().phone || '',
        joinDate: '',
        kycStatus: 'Pending',
        accountStatus: 'Active',
        role: getSessionUser().role || 'user'
    });

    const API_URL = process.env.REACT_APP_API_URL || '';
    useEffect(() => {
        const session = JSON.parse(localStorage.getItem('userSession'));
        console.log('🏗️ [FRONTEND-DASHBOARD] Initializing layout, session found:', !!session);
        if (session && session.userId) {
            const fetchUrl = `${API_URL}/api/auth/profile/${session.userId}`;
            console.log(`🌐 [FRONTEND-DASHBOARD] Fetching profile: ${fetchUrl}`);
            fetch(fetchUrl)
                .then(res => {
                    console.log(`📡 [FRONTEND-DASHBOARD] Profile response status: ${res.status}`);
                    return res.json();
                })
                .then(data => {
                    console.log('📦 [FRONTEND-DASHBOARD] Profile data:', data);
                    if (data.success) {
                        setUser(prev => ({
                            ...prev,
                            name: data.user.name || prev.name,
                            email: data.user.personal_email || data.user.email || prev.email,
                            phone: data.user.phone || prev.phone,
                            joinDate: formatMonthYear(data.user.joinDate),
                            kycStatus: data.user.kycStatus || 'In Progress',
                            accountStatus: 'Active',
                            role: data.user.role || prev.role || 'user'
                        }));
                    }
                })
                .catch(err => {
                    console.error('❌ [FRONTEND-DASHBOARD] Profile fetch error:', err);
                    setUser(prev => ({ ...prev, name: prev.name || 'Loading...' }));
                });
        }
    }, [API_URL]);

    const fetchLocation = useCallback(() => {
        const session = JSON.parse(localStorage.getItem('userSession') || 'null');
        if (!session?.userId) return; // Token is in HttpOnly cookie, so we check userId instead

        const saveLocationToBackend = (latitude, longitude, source = 'GPS') => {
            fetch(`${API_URL}/api/auth/location`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(session.token ? { 'Authorization': `Bearer ${session.token}` } : {})
                },
                credentials: 'same-origin', // App uses HttpOnly cookie for auth implicitly via browser or proxy
                body: JSON.stringify({ latitude, longitude })
            })
            .then(res => res.json())
            .then(data => {
                if (data.success) {
                    sessionStorage.setItem('locationFetched', 'true');
                    setIsLocationSaved(true);
                    console.log(`📍 [LOCATION] Saved via ${source}: (${latitude.toFixed(4)}, ${longitude.toFixed(4)})`);
                } else {
                    console.warn(`📍 [LOCATION] Server rejected location (${source}):`, data.message);
                }
            })
            .catch(err => console.error(`📍 [LOCATION] Network error saving location (${source}):`, err));
        };

        const fetchIpLocationFallback = () => {
            console.warn('📍 [LOCATION] IP fallback disabled. GPS location is strictly required.');
            alert('Location Error: GPS location is required. Please enable location permissions in your browser to proceed.');
        };

        if (!navigator.geolocation) {
            console.warn('Geolocation not supported, using IP fallback...');
            fetchIpLocationFallback();
            return;
        }

        // Add a manual timeout because Chrome sometimes hangs indefinitely on macOS
        let isResolved = false;
        const gpsTimeout = setTimeout(() => {
            if (!isResolved) {
                isResolved = true;
                console.warn('📍 [LOCATION] GPS request timed out. Using IP fallback.');
                fetchIpLocationFallback();
            }
        }, 6000);

        navigator.geolocation.getCurrentPosition(
            (position) => {
                if (isResolved) return;
                isResolved = true;
                clearTimeout(gpsTimeout);
                const { latitude, longitude } = position.coords;
                saveLocationToBackend(latitude, longitude, 'GPS');
            },
            (error) => {
                if (isResolved) return;
                isResolved = true;
                clearTimeout(gpsTimeout);
                console.warn(`📍 [LOCATION] GPS failed (code ${error.code}): ${error.message}`);
                fetchIpLocationFallback();
            },
            { enableHighAccuracy: false, timeout: 5000, maximumAge: 0 }
        );
    }, [API_URL]);

    // Request and save user location on login
    useEffect(() => {
        const locationFetched = sessionStorage.getItem('locationFetched');
        if (locationFetched !== 'true') {
            fetchLocation();
        }
    }, [fetchLocation]);

    useEffect(() => {
        const session = JSON.parse(localStorage.getItem('userSession') || 'null');
        if (!session?.userId) return;
        fetch(`${API_URL}/api/dashboard/all-data/${session.userId}`, {
            headers: session.token ? { Authorization: `Bearer ${session.token}` } : undefined
        })
            .then((res) => res.json())
            .then((data) => {
                if (data?.success) {
                    setHasActiveLoan(Number(data?.stats?.activeLoans || 0) > 0);
                    setLoanStats({
                        active: Number(data?.stats?.activeLoans || 0),
                        pending: Number(data?.stats?.pendingLoans || 0),
                        adminApproved: Number(data?.stats?.adminApprovedLoans || 0)
                    });
                }
            })
            .catch(() => {});
    }, [API_URL]);

    // SVG icon map
    const icons = {
        dashboard: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>),
        loans:    (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>),
        payments: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>),
        documents:(<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>),
        support:  (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>),
        profile:  (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>),
        kyc:      (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>),
        shield:   (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>),
        folder:   (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>),
        apply:    (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>),
        logout:   (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>),
        bell:     (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>),
        checkCircle: (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><polyline points="8 12 11 15 16 9"/></svg>),
        info:     (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><line x1="12" y1="11" x2="12" y2="16"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>),
        creditCard: (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>),
        alertTriangle: (<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3 2 20h20z"/><line x1="12" y1="9" x2="12" y2="14"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>),
        chevronDown: (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>),
    };

const navGroups = [
        {
            label: 'Main',
            links: [
                { path: '/dashboard',             label: 'Dashboard',      icon: icons.dashboard },
                { path: '/dashboard/apply-loan',  label: 'Apply for Loan', icon: icons.apply, highlight: true },
                { path: '/dashboard/loans',       label: 'My Loans',       icon: icons.loans },
                { path: '/dashboard/payments',    label: 'Payments',       icon: icons.payments },
                { path: '/dashboard/documents',   label: 'Documents',      icon: icons.documents },
                ...(hasActiveLoan || loanStats.adminApproved > 0 ? [{ path: '/dashboard/sanction-letter', label: 'Sanction Letter', icon: icons.folder }] : []),
            ]
        },
        {
            label: 'Account',
            links: [
                { path: '/dashboard/profile',    label: 'My Profile',      icon: icons.profile },
                { path: '/dashboard/kyc',        label: 'KYC',             icon: icons.kyc },
                { path: '/dashboard/support',    label: 'Support',         icon: icons.support },
            ]
        },
];


const getUserInitials = (name) => {
    if (!name || name === 'Loading...') return 'K';
    const initials = name.split(' ').map(n => n[0]).filter(Boolean).join('').toUpperCase();
    return initials || 'K';
};

const capitalizeName = (name) => {
    const value = String(name || '').trim();
    if (!value || value === 'Loading...') return value;
    return value
        .split(' ')
        .filter(Boolean)
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
};

    const adminGroup = {
        label: 'Admin',
        links: [
            { path: '/dashboard/admin/loans', label: 'Manage Loans', icon: icons.shield },
        ]
    };

    const isAdmin = user.role === 'admin' || user.role === 'super_admin';
    const allGroups = isAdmin ? [...navGroups, adminGroup] : navGroups;

    // Handle logout
    const handleLogout = useCallback(() => {
        // Save profile picture before clearing
        const profilePic = localStorage.getItem('profilePicture');

        localStorage.clear();
        sessionStorage.clear();

        // Restore profile picture
        if (profilePic) {
            localStorage.setItem('profilePicture', profilePic);
        }

        document.cookie.split(";").forEach((c) => {
            document.cookie = c
                .replace(/^ +/, "")
                .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
        });
        window.history.pushState(null, '', '/login');
        window.onpopstate = function () {
            window.history.pushState(null, '', '/login');
        };
        window.location.replace('/login');
    }, []);

    // Track activity and timeouts
    useEffect(() => {
        const updateActivity = () => {
            lastActivityRef.current = Date.now();
        };
        const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'touchmove', 'pointerdown', 'mousemove'];
        events.forEach(event => window.addEventListener(event, updateActivity, { passive: true }));

        const checkTimeout = setInterval(() => {
            const now = Date.now();
            if (now - lastActivityRef.current >= INACTIVITY_TIMEOUT) {
                alert('Logged out due to inactivity.');
                handleLogout();
            }
        }, 60000);

        return () => {
            events.forEach(event => window.removeEventListener(event, updateActivity));
            clearInterval(checkTimeout);
        };
    }, [handleLogout]);

    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (showNotifications && notificationsRef.current && !notificationsRef.current.contains(event.target)) {
                setShowNotifications(false);
            }

            if (showProfile && profileRef.current && !profileRef.current.contains(event.target)) {
                setShowProfile(false);
            }
        };

        // Use both mousedown and touchstart for iOS Safari
        document.addEventListener('mousedown', handleOutsideClick);
        document.addEventListener('touchstart', handleOutsideClick, { passive: true });

        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
            document.removeEventListener('touchstart', handleOutsideClick);
        };
    }, [showNotifications, showProfile]);

    const confirmLogout = () => {
        setShowLogoutWarning(false);
        handleLogout();
    };

    const unreadCount = notifications.filter(n => !n.read).length;
    const gamifiedProfile = {
        level: 7,
        xp: 2480,
        xpGoal: 3000,
        streak: 4,
        rank: 'Loan Legend',
        nextReward: '₹500 cashback voucher'
    };
    const xpProgress = Math.min(100, (gamifiedProfile.xp / gamifiedProfile.xpGoal) * 100);

    return (
        <div className="dashboard-container">
            {/* ── Sidebar ── */}
            <aside className={`dashboard-sidebar ${isSidebarMobileOpen ? 'mobile-open' : ''} ${isSidebarCollapsed ? 'collapsed' : ''}`}>

                {/* Brand */}
                <div className="sidebar-brand">
                    <img src={LOGO_URL} alt={LOGO_ALT} className="sidebar-logo" />
                    <button
                        className="collapse-toggle"
                        onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                        title={isSidebarCollapsed ? 'Expand' : 'Collapse'}
                    >
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            {isSidebarCollapsed
                                ? <polyline points="9 18 15 12 9 6" />
                                : <polyline points="15 18 9 12 15 6" />}
                        </svg>
                    </button>
                </div>

                {/* <div className="sidebar-quest-card">
                    <div className="quest-card-top">
                        <span className="quest-chip">Daily Quest</span>
                        <span className="quest-rank">Lv. {gamifiedProfile.level}</span>
                    </div>
                    <h3>Keep the streak alive</h3>
                    <p>Check your loans, finish KYC, and grab rewards as you move up.</p>
                    <div className="quest-progress">
                        <div className="quest-progress-track">
                            <div className="quest-progress-fill" style={{ width: `${xpProgress}%` }} />
                        </div>
                        <div className="quest-progress-meta">
                            <span>{gamifiedProfile.xp} XP</span>
                            <span>Next reward: {gamifiedProfile.nextReward}</span>
                        </div>
                    </div>
                    <div className="quest-stats">
                        <div>
                            <strong>{gamifiedProfile.streak}</strong>
                            <span>Day streak</span>
                        </div>
                        <div>
                            <strong>{gamifiedProfile.rank}</strong>
                            <span>Current rank</span>
                        </div>
                    </div>
                </div> */}

                {/* Grouped Nav */}
                <nav className="sidebar-nav">
                    {allGroups.map((group) => (
                        <div key={group.label} className="nav-group">
                            <span className="nav-group-label">{group.label}</span>
                            {group.links.map((link) => {
                                const isActive = location.pathname === link.path;
                                return (
                                    <Link
                                        key={link.path}
                                        to={link.path}
                                        className={`nav-item ${isActive ? 'active' : ''} ${link.highlight ? 'nav-highlight' : ''}`}
                                        onClick={() => setIsSidebarMobileOpen(false)}
                                        title={isSidebarCollapsed ? link.label : undefined}
                                    >
                                        <span className="nav-icon">{link.icon}</span>
                                        <span className="nav-label">{link.label}</span>
                                        {isActive && <span className="nav-pip" />}
                                    </Link>
                                );
                            })}
                        </div>
                    ))}
                </nav>

                {/* User card + Logout */}
                <div className="sidebar-footer">
                    {/* <div className="sidebar-user-card">
                        <div className="sidebar-avatar">{getUserInitials(user.name)}</div>
                        <div className="sidebar-user-info">
                            <span className="sidebar-user-name">{capitalizeName(user.name) || 'Loading...'}</span>
                            <span className="sidebar-user-role">{getRoleLabel(user.role)}</span>
                        </div>
                    </div> */}
                    {/* <button className="sidebar-logout-btn" onClick={() => setShowLogoutWarning(true)} title={isSidebarCollapsed ? 'Logout' : undefined}>
                        <span className="nav-icon">{icons.logout}</span>
                        <span className="nav-label">Sign Out</span>
                    </button> */}
                </div>
            </aside>

            {/* Main Content Area */}
            <div className={`dashboard-wrapper ${isSidebarCollapsed ? 'collapsed' : ''}`}>
                {/* Top Header */}
                <header className="dashboard-header-new">
                    <div className="header-left">
                        <button className="mobile-toggle" onClick={() => setIsSidebarMobileOpen(!isSidebarMobileOpen)}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
                        </button>
                        <h2 className="page-title">
                            {allGroups.flatMap(g => g.links).find(l => l.path === location.pathname)?.label || 'Dashboard'}
                        </h2>
                    </div>

                    <div className="header-right">
                        {!isLocationSaved && (
                            <button 
                                onClick={fetchLocation}
                                style={{
                                    background: 'rgba(162,59,46,0.08)', color: '#A23B2E', border: '1px solid rgba(162,59,46,0.25)',
                                    padding: '6px 12px', borderRadius: '20px', fontSize: '13px',
                                    fontWeight: '500', marginRight: '16px', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', gap: '6px'
                                }}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
                                Save Location
                            </button>
                        )}
                        <div className="header-actions">
                            <div ref={notificationsRef}>
                                <button className="header-btn" onClick={() => {
                                    setShowNotifications(!showNotifications);
                                    setShowProfile(false);
                                }}>
                                    <span className="icon">{icons.bell}</span>
                                    {unreadCount > 0 && <span className="badge">{unreadCount}</span>}
                                </button>

                                {showNotifications && (
                                    <div className="header-dropdown notifications-dropdown">
                                        <div className="dropdown-header">
                                            <h3>Notifications</h3>
                                            <button className="clear-all">Clear All</button>
                                        </div>
                                        <div className="dropdown-list">
                                            {notifications.map(n => (
                                                <div key={n.id} className={`dropdown-item ${n.read ? 'read' : 'unread'}`}>
                                                    <div className="item-icon">{n.type === 'payment' ? icons.creditCard : n.type === 'success' ? icons.checkCircle : icons.info}</div>
                                                    <div className="item-body">
                                                        <h4>{n.title}</h4>
                                                        <p>{n.message}</p>
                                                        <span className="time">{n.time}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div ref={profileRef}>
                                <button className={`header-profile-btn ${showProfile ? 'open' : ''}`} onClick={() => {
                                    setShowProfile(!showProfile);
                                    setShowNotifications(false);
                                }}>
                                    <div className="avatar">
                                        {getUserInitials(user.name)}
                                    </div>
                                    <div className="name-info">
                                        <span className="user-name">{capitalizeName(user.name)}</span>
                                    </div>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="profile-chevron">
                                        <polyline points="6 9 12 15 18 9"></polyline>
                                    </svg>
                                </button>
                                {showProfile && (
                                    <div className="header-dropdown profile-dropdown">
                                        <div className="profile-top">
                                            <div className="avatar-large">
                                                {user.name && user.name !== 'Loading...'
                                                    ? user.name.split(' ').map(n => n[0]).filter(Boolean).join('').toUpperCase()
                                                    : icons.profile}
                                            </div>
                                            <h4>{user.name}</h4>
                                            <p>{user.email}</p>
                                        </div>
                                        <div className="profile-menu">
                                            <Link to="/dashboard/profile" className="menu-item" onClick={() => setShowProfile(false)}>
                                                <span className="menu-item-icon">{icons.profile}</span> My Profile
                                            </Link>
                                            <button className="menu-item" onClick={() => setShowLogoutWarning(true)}>
                                                <span className="menu-item-icon">{icons.logout}</span> Logout
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </header>

                <main className="dashboard-content-new">
                    <Outlet />
                </main>

                {/* Mobile Bottom Navigation */}
                <div className="mobile-bottom-nav">
                    <Link to="/dashboard" className={`bottom-nav-item ${location.pathname === '/dashboard' ? 'active' : ''}`}>
                        <span className="nav-icon">{icons.dashboard}</span>
                        <span>Home</span>
                    </Link>
                    <Link to="/dashboard/loans" className={`bottom-nav-item ${location.pathname === '/dashboard/loans' ? 'active' : ''}`}>
                        <span className="nav-icon">{icons.loans}</span>
                        <span>Loans</span>
                    </Link>
                    <Link to="/dashboard/payments" className={`bottom-nav-item ${location.pathname === '/dashboard/payments' ? 'active' : ''}`}>
                        <span className="nav-icon">{icons.payments}</span>
                        <span>Pay</span>
                    </Link>
                    <Link to="/dashboard/support" className={`bottom-nav-item ${location.pathname === '/dashboard/support' ? 'active' : ''}`}>
                        <span className="nav-icon">{icons.support}</span>
                        <span>Help</span>
                    </Link>
                </div>
            </div>

            {/* Overlay for mobile sidebar */}
            {isSidebarMobileOpen && <div className="sidebar-overlay" onClick={() => setIsSidebarMobileOpen(false)}></div>}

            {/* Logout Warning Modal */}
            {showLogoutWarning && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-icon warning">{icons.alertTriangle}</div>
                        <h3>Confirm Logout</h3>
                        <p>Are you sure you want to log out of your dashboard?</p>
                        <div className="modal-actions">
                            <button className="modal-btn cancel" onClick={() => setShowLogoutWarning(false)}>
                                Cancel
                            </button>
                            <button className="modal-btn confirm" onClick={confirmLogout}>
                                Yes, Logout
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}

export default DashboardLayout;
